import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SelectOption } from '@nyce/admin-kit/ui';
import { useAsync } from '../../../components/common';
import { knowledgeFor, type NamedRef } from '../../api/knowledge';
import '../../buddy.css';
import './knowledge.css';

/** One page's view in the URL query (`?tab=…&skill=…`); `set` patches it, '' / null removes a key, `target` stays. */
export function useQueryState() {
  const [params, setParams] = useSearchParams();
  const set = useCallback(
    (patch: Record<string, string | number | null | undefined>) =>
      setParams(
        (cur) => {
          const next = new URLSearchParams(cur);
          for (const [k, v] of Object.entries(patch))
            if (v == null || v === '') next.delete(k);
            else next.set(k, String(v));
          return next;
        },
        { replace: true }
      ),
    [setParams]
  );
  return { get: (k: string) => params.get(k) ?? '', set };
}

/** Systems, organizations or tenants of the environment, for pickers; empty on failure (callers fall back to text). */
export function useLookup(target: string, kind: 'systems' | 'organizations' | 'tenants' | null) {
  return useAsync(
    () =>
      kind
        ? knowledgeFor(target)
            .lookups[kind]()
            .catch(() => [] as NamedRef[])
        : Promise.resolve([]),
    [target, kind]
  );
}

export const refOptions = (rows: NamedRef[] | null): SelectOption[] =>
  [...(rows ?? [])]
    .sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id))
    .map((r) => ({ value: r.id, label: r.name && r.name !== r.id ? `${r.name} (${r.id})` : r.id }));

/** Tone class of a status word (validation outcome, crawl state, document status). */
export const toneOf = (s: string) =>
  s === 'passed' || s === 'ready' || s === 'enabled' || s === 'on'
    ? 'nyk-tone-green'
    : s === 'repaired' || s === 'warned' || s === 'pending' || s === 'indexing' || s === 'crawling…'
      ? 'nyk-tone-amber'
      : s === 'errored' || s === 'failed'
        ? 'nyk-tone-red'
        : 'nyk-tone-grey';

export const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : '—');
